import React, { useEffect, useRef } from 'react';

export type MemberDetailTabItem = {
    key: string;
    label: string;
    /** 없으면 라벨만 그린다. 아이콘이 어울리지 않는 화면도 이 탭바를 쓸 수 있게 선택으로 둔다. */
    icon?: React.ReactNode;
    count?: number;
};

type Props = {
    tabs: MemberDetailTabItem[];
    activeTab: string;
    onChange: (key: string) => void;
    /** 스크린리더가 읽는 탭 목록 이름. 화면마다 달라야 해서 밖에서 준다. */
    ariaLabel?: string;
    /** tab/tabpanel 을 잇는 id 접두사. 한 페이지에 탭바가 둘이어도 id 가 겹치지 않는다. */
    idPrefix?: string;
    /**
     * 'underline' — 회원 상세의 밑줄 탭(원래 모양, 기본값)
     * 'pill'      — OpsBriefingLayout·CenterReportLayout 의 알약 탭
     */
    variant?: 'underline' | 'pill';
};

const MemberDetailTabBar = ({
    tabs,
    activeTab,
    onChange,
    ariaLabel = '회원 상세 메뉴',
    idPrefix = 'member-detail',
    variant = 'underline',
}: Props) => {
    const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

    useEffect(() => {
        const activeElement = tabRefs.current[activeTab];
        if (activeElement && typeof activeElement.scrollIntoView === 'function') {
            activeElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        }
    }, [activeTab]);

    const moveSelection = (currentIndex: number, event: React.KeyboardEvent<HTMLButtonElement>) => {
        let targetIndex: number | null = null;
        if (event.key === 'ArrowRight') targetIndex = (currentIndex + 1) % tabs.length;
        if (event.key === 'ArrowLeft') targetIndex = (currentIndex - 1 + tabs.length) % tabs.length;
        if (event.key === 'Home') targetIndex = 0;
        if (event.key === 'End') targetIndex = tabs.length - 1;
        if (targetIndex === null) return;

        event.preventDefault();
        const target = tabs[targetIndex];
        onChange(target.key);
        requestAnimationFrame(() => tabRefs.current[target.key]?.focus());
    };

    const isPill = variant === 'pill';

    return (
        <div
            role="tablist"
            aria-label={ariaLabel}
            style={
                isPill
                    ? {
                          display: 'inline-flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 6,
                          borderRadius: 16,
                          border: '1px solid #E2E8F0',
                          backgroundColor: '#FFFFFF',
                          padding: 6,
                          boxShadow: '0 8px 24px rgba(26,48,93,0.06)',
                      }
                    : {
                          display: 'flex',
                          flexWrap: 'nowrap',
                          overflowX: 'auto',
                          borderBottom: '1px solid #F3F4F6',
                          padding: '0 12px',
                          WebkitOverflowScrolling: 'touch',
                      }
            }
        >
            {tabs.map((tab, index) => {
                const isActive = activeTab === tab.key;
                return (
                    <button
                        key={tab.key}
                        ref={(element) => {
                            tabRefs.current[tab.key] = element;
                        }}
                        type="button"
                        role="tab"
                        id={`${idPrefix}-tab-${tab.key}`}
                        aria-controls={`${idPrefix}-panel-${tab.key}`}
                        aria-selected={isActive}
                        tabIndex={isActive ? 0 : -1}
                        onClick={() => onChange(tab.key)}
                        onKeyDown={(event) => moveSelection(index, event)}
                        style={
                            isPill
                                ? {
                                      height: 44,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 8,
                                      padding: '0 22px',
                                      borderRadius: 11,
                                      fontSize: 16,
                                      fontWeight: isActive ? 700 : 500,
                                      color: isActive ? '#FFFFFF' : '#8B95A5',
                                      whiteSpace: 'nowrap',
                                      backgroundColor: isActive ? '#1A305D' : 'transparent',
                                      border: 'none',
                                      boxShadow: isActive ? '0 6px 16px rgba(26,48,93,0.18)' : 'none',
                                      cursor: 'pointer',
                                      transition: 'color 0.15s, background-color 0.15s',
                                  }
                                : {
                                      flex: '1 0 130px',
                                      minWidth: 130,
                                      minHeight: 66,
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      gap: 8,
                                      padding: '18px 12px 16px',
                                      fontSize: 15,
                                      fontWeight: isActive ? 700 : 500,
                                      color: isActive ? '#111827' : '#6B7280',
                                      whiteSpace: 'nowrap',
                                      backgroundColor: 'transparent',
                                      border: 'none',
                                      borderBottom: isActive ? '3px solid #111827' : '3px solid transparent',
                                      cursor: 'pointer',
                                      transition: 'color 0.15s, border-color 0.15s',
                                  }
                        }
                    >
                        {tab.icon !== undefined && (
                            <span aria-hidden="true" style={{ display: 'inline-flex', fontSize: 18, lineHeight: 1 }}>
                                {tab.icon}
                            </span>
                        )}
                        <span>{tab.label}</span>
                        {tab.count !== undefined && (
                            <span
                                aria-label={`${tab.count}건`}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: 24,
                                    height: 24,
                                    padding: '0 7px',
                                    borderRadius: 999,
                                    fontSize: 12,
                                    fontWeight: 700,
                                    backgroundColor: isActive ? '#111827' : '#E5E7EB',
                                    color: isActive ? '#FFFFFF' : '#6B7280',
                                }}
                            >
                                {tab.count}
                            </span>
                        )}
                    </button>
                );
            })}
        </div>
    );
};

export default MemberDetailTabBar;
